import { Popover, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParticipant, usePubSub, VideoPlayer } from "../config/sdkConfig";
import { useMediaQuery } from "react-responsive";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import useWindowSize from "../hooks/useWindowSize";
import MicOffSmallIcon from "../icons/MicOffSmallIcon";
import SpeakerIcon from "../icons/SpeakerIcon";
import { getQualityScore, nameTructed } from "../utils/common";
import * as ReactDOM from "react-dom";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { useSiteContext } from "../context/SiteContext";
import AgoraContext, { stringToNumericUid } from "../context/AgoraContext";
import { useAgoraChat } from "../context/AgoraChatContext";
import { useMeetingState } from "../context/MeetingStateContext";
import { StatusBadge } from "./studio/reactions";
import TileReaction from "./studio/reactions/TileReaction";
import { VideoFrameOverlay } from "./studio/overlays/BrandingOverlays";
import ReactPlayer from "react-player";

export const CornerDisplayName = ({
  participantId,
  isPresenting,
  displayName,
  isLocal,
  micOn,
  mouseOver,
  isActiveSpeaker,
}) => {
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  // Get branding settings from context
  const { branding, setBranding, participantBranding, recordingStartedById } = useMeetingAppContext();
  // Show a small red "Recording" pill next to the recorder's name on their
  // tile, mirroring the badge in the participants panel. The recorder ID
  // comes from the RTM senderId (Mongo userId, string), but remote tiles
  // are keyed by Agora numeric UID — so we also try the hash-derived UID
  // to bridge the two namespaces. (Mirror of the same logic in
  // ParticipantsPanel.ParticipantItem.)
  let isRecorder = false;
  if (recordingStartedById != null) {
    const recIdStr = String(recordingStartedById);
    if (recIdStr === String(participantId)) {
      isRecorder = true;
    } else {
      try {
        const derivedUid = stringToNumericUid(recIdStr);
        if (String(derivedUid) === String(participantId)) isRecorder = true;
      } catch (_) { /* ignore */ }
    }
  }

  // Inline name editing state (local participant only)
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const editNameInputRef = useRef(null);

  // PubSub for broadcasting name changes to all participants
  const { publish: publishDisplayNameChange } = usePubSub("DISPLAY_NAME_CHANGE");
  // Access setParticipantName from Agora context to update the actual join-time name variable
  const agoraCtx = useContext(AgoraContext);
  // Access setChatUserName to update the chat sender name
  const { setChatUserName } = useAgoraChat();

  const handleStartEditName = useCallback((e) => {
    e.stopPropagation();
    setEditNameValue(displayName || '');
    setIsEditingName(true);
    setTimeout(() => editNameInputRef.current?.focus(), 50);
  }, [displayName]);

  const handleSaveName = useCallback(() => {
    const trimmed = editNameValue.trim();
    if (trimmed && trimmed !== displayName) {
      // 1. Update the actual participantName in AgoraContext (same var used at join time)
      //    This updates local tile + attendees list + backend re-registration
      agoraCtx?.setParticipantName?.(trimmed);
      // 2. Update chat sender name so new messages show the updated name
      setChatUserName?.(trimmed);
      // 3. Also update branding lower third so it stays in sync
      setBranding(prev => ({
        ...prev,
        lowerThirdName: trimmed,
        showLowerThird: true,
      }));
      // 4. Broadcast to all other participants via PubSub for instant update
      publishDisplayNameChange(JSON.stringify({ newName: trimmed }));
    }
    setIsEditingName(false);
  }, [editNameValue, displayName, setBranding, publishDisplayNameChange, agoraCtx, setChatUserName]);

  const handleCancelEditName = useCallback(() => {
    setIsEditingName(false);
  }, []);

  // For lower third name/title, use per-participant branding if available (for remote participants)
  const ptBranding = !isLocal ? participantBranding?.[participantId] || participantBranding?.[String(participantId)] || participantBranding?.[stringToNumericUid(participantId)] : null;

  const { height: windowHeight } = useWindowSize();

  const [statsBoxHeightRef, setStatsBoxHeightRef] = useState(null);
  const [statsBoxWidthRef, setStatsBoxWidthRef] = useState(null);

  const [coords, setCoords] = useState({}); // takes current button coordinates

  const statsBoxHeight = useMemo(
    () => statsBoxHeightRef?.offsetHeight,
    [statsBoxHeightRef]
  );

  const statsBoxWidth = useMemo(
    () => statsBoxWidthRef?.offsetWidth,
    [statsBoxWidthRef]
  );

  const analyzerSize = isXLDesktop
    ? 32
    : isLGDesktop
    ? 28
    : isTab
    ? 24
    : isMobile
    ? 20
    : 18;

  const show = useMemo(() => {
    return true; 
  }, []);

  const {
    webcamStream,
    micStream,
    screenShareStream,
    getVideoStats,
    getAudioStats,
    getShareStats,
    getShareAudioStats,
    participant,
  } = useParticipant(participantId);

  const statsIntervalIdRef = useRef();
  const [score, setScore] = useState({});
  const [audioStats, setAudioStats] = useState({});
  const [videoStats, setVideoStats] = useState({});

  const updateStats = async () => {
    let stats = [];
    let audioStats = [];
    let videoStats = [];
    if (isPresenting) {
      stats = await getShareStats();
    } else if (webcamStream) {
      stats = await getVideoStats();
    } else if (micStream) {
      stats = await getAudioStats();
    }

    if (webcamStream || micStream || isPresenting) {
      videoStats = isPresenting ? await getShareStats() : await getVideoStats();
      audioStats = isPresenting
        ? await getShareAudioStats()
        : await getAudioStats();
    }

    let score = stats
      ? stats.length > 0
        ? getQualityScore(stats[0])
        : 100
      : 100;

    setScore(score);
    setAudioStats(audioStats);
    setVideoStats(videoStats);
  };

  const qualityStateArray = [
    { label: "", audio: "Audio", video: "Video" },
    {
      label: "Latency",
      audio:
        audioStats && audioStats[0]?.rtt ? `${audioStats[0]?.rtt} ms` : "-",
      video:
        videoStats && videoStats[0]?.rtt ? `${videoStats[0]?.rtt} ms` : "-",
    },
    {
      label: "Jitter",
      audio:
        audioStats && audioStats[0]?.jitter
          ? `${parseFloat(audioStats[0]?.jitter).toFixed(2)} ms`
          : "-",
      video:
        videoStats && videoStats[0]?.jitter
          ? `${parseFloat(videoStats[0]?.jitter).toFixed(2)} ms`
          : "-",
    },
    {
      label: "Packet Loss",
      audio: audioStats
        ? audioStats[0]?.packetsLost
          ? `${parseFloat(
              (audioStats[0]?.packetsLost * 100) / audioStats[0]?.totalPackets
            ).toFixed(2)}%`
          : "-"
        : "-",
      video: videoStats
        ? videoStats[0]?.packetsLost
          ? `${parseFloat(
              (videoStats[0]?.packetsLost * 100) / videoStats[0]?.totalPackets
            ).toFixed(2)}%`
          : "-"
        : "-",
    },
    {
      label: "Bitrate",
      audio:
        audioStats && audioStats[0]?.bitrate
          ? `${parseFloat(audioStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
      video:
        videoStats && videoStats[0]?.bitrate
          ? `${parseFloat(videoStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
    },
    {
      label: "Frame rate",
      audio: "-",
      video:
        videoStats &&
        (videoStats[0]?.size?.framerate === null ||
          videoStats[0]?.size?.framerate === undefined)
          ? "-"
          : `${videoStats ? videoStats[0]?.size?.framerate : "-"}`,
    },
    {
      label: "Resolution",
      audio: "-",
      video: videoStats
        ? videoStats && videoStats[0]?.size?.width === null
          ? "-"
          : `${videoStats[0]?.size?.width}x${videoStats[0]?.size?.height}`
        : "-",
    },
    {
      label: "Codec",
      audio: audioStats && audioStats[0]?.codec ? audioStats[0]?.codec : "-",
      video: videoStats && videoStats[0]?.codec ? videoStats[0]?.codec : "-",
    },
    {
      label: "Cur. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.currentSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.currentSpatialLayer || 0} T:${
                videoStats[0]?.currentTemporalLayer || 0
              }`
          : "-",
    },
    {
      label: "Pref. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.preferredSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.preferredSpatialLayer || 0} T:${
                videoStats[0]?.preferredTemporalLayer || 0
              }`
          : "-",
    },
  ];

  useEffect(() => {
    if (webcamStream || micStream || screenShareStream) {
      updateStats();

      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
      }

      // Disable stats polling entirely on mobile — it fires getVideoStats/getAudioStats
      // per participant every few seconds, causing state updates that compete with audio
      const isMobile = window.matchMedia?.('(max-width: 767px)')?.matches;
      if (!isMobile) {
        statsIntervalIdRef.current = setInterval(updateStats, 500);
      }
    } else {
      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
        statsIntervalIdRef.current = null;
      }
    }

    return () => {
      if (statsIntervalIdRef.current) clearInterval(statsIntervalIdRef.current);
    };
  }, [webcamStream, micStream, screenShareStream]);

  // Build lower third style based on branding settings
  const getLowerThirdStyle = () => {
    const baseStyle = {
      backdropFilter: "blur(4px)",
      transition: "all 200ms",
      transitionTimingFunction: "linear",
      transform: `scale(${show ? 1 : 0})`,
      fontFamily: branding?.font || 'Inter',
    };

    // Shape-based border radius
    const shapeRadius = {
      bubble: '999px',
      classic: '12px',
      minimal: '4px',
      block: '0px',
    };
    baseStyle.borderRadius = shapeRadius[branding?.lowerThirdShape] || '6px';

    // Template-specific styling
    const template = branding?.lowerThirdTemplate || 'modern';

    // Background based on template
    if (branding?.lowerThirdBackground !== false) {
      if (template === 'classic' || template === 'bold') {
        // Full gradient background
        baseStyle.background = `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}cc, ${branding?.accentColor || '#00c853'}cc)`;
      } else if (template === 'broadcast') {
        // News style - solid color with accent
        baseStyle.background = `${branding?.lowerThirdBgColor || '#000000'}e6`;
        baseStyle.borderLeft = `4px solid ${branding?.primaryColor || '#6a1fbf'}`;
      } else if (template === 'corporate') {
        // Two-tone split
        baseStyle.background = `linear-gradient(90deg, ${branding?.primaryColor || '#6a1fbf'}cc 0%, ${branding?.lowerThirdBgColor || '#000000'}cc 30%)`;
      } else {
        // Default/minimal/modern/elegant/creative
        baseStyle.background = branding?.lowerThirdBgColor
          ? `${branding.lowerThirdBgColor}b3`
          : `${branding?.primaryColor || '#6a1fbf'}80`;
      }
    } else {
      baseStyle.background = 'transparent';
    }

    // Border
    if (branding?.lowerThirdBorder) {
      baseStyle.border = `2px solid ${branding?.lowerThirdBorderColor || branding?.primaryColor || '#6a1fbf'}`;
    }

    // Underline (for creative template or manual toggle).  Also flatten the
    // bottom corners and add a bit of paddingBottom so the 3px line actually
    // reads as an underline instead of being swallowed by the rounded
    // container edge + tight bottom padding.  This mirrors the Branding
    // Panel's LowerThirdPreview so the preview matches the live tile.
    if (branding?.lowerThirdUnderline || template === 'creative') {
      baseStyle.borderBottom = `3px solid ${branding?.primaryColor || '#6a1fbf'}`;
      baseStyle.paddingBottom = '6px';
      // Keep top corners rounded but square off the bottom so the underline
      // reads as a flat line rather than a curved pill edge.
      const topRadius = baseStyle.borderRadius || '6px';
      baseStyle.borderTopLeftRadius = topRadius;
      baseStyle.borderTopRightRadius = topRadius;
      baseStyle.borderBottomLeftRadius = '0';
      baseStyle.borderBottomRightRadius = '0';
      // Strip the shorthand so the long-hand values above take effect.
      delete baseStyle.borderRadius;
    }

    // Animation
    const animation = branding?.lowerThirdAnimation || 'none';
    if (animation !== 'none') {
      baseStyle.animation = `lowerThird${animation} 0.5s ease-out`;
    }

    return baseStyle;
  };

  // Get text colors from branding
  const nameColor = branding?.lowerThirdTextColor || '#ffffff';
  const titleColor = branding?.lowerThirdTitleColor || '#9ca3af';
  const template = branding?.lowerThirdTemplate || 'modern';

  // Determine the display name - use lowerThirdName if showLowerThird is enabled and name is set
  const getDisplayName = () => {
    if (isPresenting) {
      return isLocal ? 'You are presenting' : `${nameTructed(displayName, 15)} is presenting`;
    }
    // Use custom lower third name if enabled and set
    if (isLocal && branding?.showLowerThird && branding?.lowerThirdName) {
      return branding.lowerThirdName;
    }
    // For remote participants, use their per-participant lower third name
    if (!isLocal && ptBranding?.showLowerThird && ptBranding?.lowerThirdName) {
      return ptBranding.lowerThirdName;
    }
    return nameTructed(displayName, 26);
  };

  return (
    <>
      {/* CSS Animations for lower third and active speaker */}
      <style>{`
        @keyframes lowerThirdfade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lowerThirdslide-left {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes lowerThirdslide-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes lowerThirdslide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes lowerThirdbounce {
          0% { transform: scale(0); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        .active-speaker-glow {
          box-shadow: 0 0 30px rgba(34, 197, 94, 0.6), 0 0 60px rgba(34, 197, 94, 0.3), 0 10px 40px rgba(0,0,0,0.3);
          border-color: rgba(34, 197, 94, 1);
        }
        .active-speaker-glow::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 16px;
          border: 2px solid rgba(34, 197, 94, 0.6);
          opacity: 0.8;
          pointer-events: none;
        }
      `}</style>

      <div
        className="absolute bottom-1 left-1 md:bottom-2 md:left-2 flex items-center gap-1 md:gap-1.5 px-1.5 py-0.5 md:px-2 md:py-1 z-10"
        style={getLowerThirdStyle()}
      >
        {/* Accent bar for modern/minimal templates */}
        {(template === 'modern' || template === 'minimal') && (
          <div
            className="w-1 h-4 mr-1 rounded-full"
            style={{ backgroundColor: branding?.primaryColor || '#6a1fbf' }}
          />
        )}

        {/* Elegant template - brackets */}
        {template === 'elegant' && (
          <span className="text-xs mr-1" style={{ color: branding?.primaryColor || '#6a1fbf' }}>[</span>
        )}

        {/* Mic/Speaker icon */}
        {!micOn && !isPresenting ? (
          <MicOffSmallIcon fillcolor="#FF4B4B" />
        ) : micOn && isActiveSpeaker ? (
          <SpeakerIcon />
        ) : null}

        {/* Recording-owner pill — surfaces the participant who started the
            current recording on their own video tile so others know who owns
            the capture without opening the participants panel. */}
        {isRecorder && (
          <span
            className="flex items-center gap-1 mr-1 px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-[9px] md:text-[10px] font-semibold text-red-200"
            title="Started this recording"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
            </span>
            REC
          </span>
        )}

        {/* Name with inline edit for local participant */}
        {isLocal && isEditingName ? (
          <input
            ref={editNameInputRef}
            type="text"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveName();
              if (e.key === 'Escape') handleCancelEditName();
            }}
            onBlur={handleSaveName}
            maxLength={30}
            className="bg-white/20 text-white text-[10px] md:text-xs font-medium rounded px-1 py-0 outline-none border border-white/30 focus:border-white/60 w-24 md:w-32"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`group/name font-medium flex items-center gap-0.5 ${template === 'bold' ? 'text-sm md:text-base font-bold' : 'text-[10px] md:text-xs'} ${isLocal ? 'cursor-pointer' : ''}`}
            style={{
              color: nameColor,
              ...(template === 'bold' && {
                background: `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}, ${branding?.accentColor || '#00c853'})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }),
            }}
            onClick={isLocal ? handleStartEditName : undefined}
          >
            {getDisplayName()}
            {isLocal && !isPresenting && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-2.5 h-2.5 md:w-3 md:h-3 opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
              >
                <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.05 10.476a1.75 1.75 0 0 0-.46.84l-.46 2.3a.75.75 0 0 0 .882.882l2.3-.46a1.75 1.75 0 0 0 .84-.46l7.963-7.963a1.75 1.75 0 0 0 0-2.475l-.627-.627ZM11.72 3.22a.25.25 0 0 1 .354 0l.627.627a.25.25 0 0 1 0 .354L11.5 5.4 10.1 4l1.22-1.28h.4ZM9.4 4.7l1.4 1.4-5.898 5.898a.25.25 0 0 1-.12.066l-1.528.306.306-1.528a.25.25 0 0 1 .066-.12L9.4 4.7Z" />
              </svg>
            )}
          </span>
        )}

        {/* Elegant template - closing bracket */}
        {template === 'elegant' && (
          <span className="text-xs ml-1" style={{ color: branding?.primaryColor || '#6a1fbf' }}>]</span>
        )}

        {/* Show title from branding if set and showLowerThird is enabled.
            Title sits next to the name — it must stay readable at the same
            breakpoints where the name is readable.  The earlier 9/10px sizes
            rendered the title as a faint gray sliver on large desktop tiles;
            scale it alongside the name so desktop users can actually read it. */}
        {branding?.showLowerThird && isLocal && branding?.lowerThirdTitle && (
          <span
            className={`ml-1 whitespace-nowrap ${template === 'bold' ? 'text-xs md:text-sm' : 'text-[10px] md:text-xs'}`}
            style={{ color: titleColor, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
          >
            {template === 'broadcast' ? `| ${branding.lowerThirdTitle}` : branding.lowerThirdTitle}
          </span>
        )}
        {/* Show title for remote participants from their per-participant branding */}
        {!isLocal && ptBranding?.showLowerThird && ptBranding?.lowerThirdTitle && (
          <span
            className={`ml-1 whitespace-nowrap ${template === 'bold' ? 'text-xs md:text-sm' : 'text-[10px] md:text-xs'}`}
            style={{ color: titleColor, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
          >
            {template === 'broadcast' ? `| ${ptBranding.lowerThirdTitle}` : ptBranding.lowerThirdTitle}
          </span>
        )}
      </div>

      {(webcamStream || micStream || screenShareStream) && (
        <div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="absolute bottom-2 right-2 rounded-md p-2 cursor-pointer z-[5]"
          >
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button
                    className="rounded-full flex items-center justify-center cursor-pointer"
                    style={{
                      backgroundColor:
                        score > 7
                          ? "#3BA55D"
                          : score > 4
                          ? "#faa713"
                          : "#FF5D5D",
                      width: "12px",
                      height: "12px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCoords(e.target.getBoundingClientRect());
                    }}
                  >
                    <div></div>
                  </Popover.Button>
                  {ReactDOM.createPortal(
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel
                        static
                        style={{
                          zIndex: 9999,
                          position: "fixed",
                          bottom: coords.bottom != null ? `${window.innerHeight - coords.top + 8}px` : "auto",
                          right: coords.right != null ? `${window.innerWidth - coords.right}px` : "auto",
                        }}
                      >
                        <div
                          ref={setStatsBoxWidthRef}
                        >
                          <div
                            ref={setStatsBoxHeightRef}
                            className="bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                          >
                            <div
                              className={`p-[9px] flex items-center justify-between rounded-t-lg`}
                              style={{
                                backgroundColor:
                                  score > 7
                                    ? "#3BA55D"
                                    : score > 4
                                    ? "#faa713"
                                    : "#FF5D5D",
                              }}
                            >
                              <p className="text-sm text-white font-semibold">{`Quality Score : ${
                                score > 7
                                  ? "Good"
                                  : score > 4
                                  ? "Average"
                                  : "Poor"
                              }`}</p>

                              <button
                                className="cursor-pointer text-white hover:bg-[#ffffff33] rounded-full px-1 text-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  close();
                                }}
                              >
                                <XMarkIcon
                                  className="text-white"
                                  style={{ height: 16, width: 16 }}
                                />
                              </button>
                            </div>
                            <div className="flex">
                              <div className="flex flex-col">
                                {qualityStateArray.map((item, index) => {
                                  return (
                                    <div
                                      className="flex"
                                      key={index}
                                      style={{
                                        borderBottom:
                                          index === qualityStateArray.length - 1
                                            ? ""
                                            : `1px solid #ffffff33`,
                                      }}
                                    >
                                      <div className="flex flex-1 items-center w-[120px]">
                                        {index !== 0 && (
                                          <p className="text-xs text-white my-[6px] ml-2">
                                            {item.label}
                                          </p>
                                        )}
                                      </div>
                                      <div
                                        className="flex flex-1 items-center justify-center"
                                        style={{
                                          borderLeft: `1px solid #ffffff33`,
                                        }}
                                      >
                                        <p className="text-xs text-white my-[6px] w-[80px] text-center">
                                          {item.audio}
                                        </p>
                                      </div>
                                      <div
                                        className="flex flex-1 items-center justify-center"
                                        style={{
                                          borderLeft: `1px solid #ffffff33`,
                                        }}
                                      >
                                        <p className="text-xs text-white my-[6px] w-[80px] text-center">
                                          {item.video}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>,
                    document.body
                  )}
                </>
              )}
            </Popover>
          </div>
        </div>
      )}
    </>
  );
};

export function ParticipantView({ participantId, isSingleParticipant = false }) {
  const {
    displayName,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    mode,
    isActiveSpeaker,
    participant,
    pinState,
  } = useParticipant(participantId);

  const { localPinnedParticipant, activeElement, setActiveElement, meetingRole, meetingSettings, isCoHost } = useSiteContext();
  const isMobile = useIsMobile();
  const { selectedSpeaker, raisedHandsParticipants, brbParticipants, participantReactions, branding, participantBranding } = useMeetingAppContext();
  const agoraCtx = useContext(AgoraContext);
  const pinParticipant = agoraCtx?.pin;
  const unpinParticipant = agoraCtx?.unpin;
  const pinnedParticipants = agoraCtx?.pinnedParticipants;
  const isPinned = pinnedParticipants?.has(participantId) || pinnedParticipants?.has(String(participantId)) || pinnedParticipants?.has(Number(participantId));
  const meetingState = useMeetingState();
  const isSpotlighted = meetingState?.spotlightedParticipants?.some(
    s => s.participantId === participantId || s.participantId === String(participantId)
  );

  // Check if this participant has an active video element playing on their tile (PiP mode only)
  // For fullscreen and background modes, VideoSection renders in MeetingContainerNew
  const videoDisplayMode = activeElement?.type === "video"
    ? (typeof activeElement.settings === 'string'
        ? JSON.parse(activeElement.settings)?.display_mode
        : activeElement.settings?.display_mode) || 'fullscreen'
    : null;
  // Match sender to participant tile using both string ID and numeric Agora UID
  // Local participant uses string ID, remote participants use numeric UID from stringToNumericUid()
  const senderIdStr = activeElement?.senderParticipantId;
  const senderNumericUid = senderIdStr ? stringToNumericUid(senderIdStr) : null;
  const isSenderTile = senderIdStr === participantId
    || String(senderNumericUid) === String(participantId)
    || String(senderIdStr) === String(participantId);
  const hasVideoElement = activeElement?.type === "video" && isSenderTile && videoDisplayMode === 'pip';

  // Video element state
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const videoPlayerRef = useRef(null);

  // Parse video settings
  const videoSettings = useMemo(() => {
    if (!hasVideoElement || !activeElement?.settings) return null;
    const settings = typeof activeElement.settings === 'string'
      ? JSON.parse(activeElement.settings)
      : activeElement.settings;
    return settings;
  }, [hasVideoElement, activeElement?.settings]);

  // Get video URL
  const videoUrl = useMemo(() => {
    if (!videoSettings) return "";
    const url = videoSettings.file_url || videoSettings.fileUrl || "";
    if (!url) return "";
    return url.startsWith("http") ? url : `https://${url}`;
  }, [videoSettings]);

  // Video playback settings
  const videoAutoplay = videoSettings?.playback?.autoplay !== false;
  const videoLoop = videoSettings?.playback?.loop || false;
  const videoAudioMode = videoSettings?.playback?.audio_mode || "normal";
  const videoVolume = videoAudioMode === "muted" ? 0 : (videoSettings?.playback?.volume || 100) / 100;
  const videoStartAt = videoSettings?.playback?.start_at || 0;
  const videoEndAt = videoSettings?.playback?.end_at || null;

  // Video PiP settings (position and size)
  const videoPipPosition = videoSettings?.pip_settings?.position || 'bottom_right';
  const videoPipSize = videoSettings?.pip_settings?.size || 25; // Default 25%

  // Handle video ready
  const handleVideoReady = useCallback(() => {
    setVideoReady(true);
    // Seek to start position if specified
    if (videoStartAt > 0 && videoPlayerRef.current) {
      videoPlayerRef.current.seekTo(videoStartAt, "seconds");
    }
    if (videoAutoplay) {
      setVideoPlaying(true);
    }
  }, [videoAutoplay, videoStartAt]);

  // Handle video ended
  const handleVideoEnded = useCallback(() => {
    if (videoLoop) {
      videoPlayerRef.current?.seekTo(videoStartAt, "seconds");
      setVideoPlaying(true);
    } else {
      setVideoPlaying(false);
    }
  }, [videoLoop, videoStartAt]);

  // Handle video progress - check for end time
  const handleVideoProgress = useCallback((state) => {
    if (videoEndAt && state.playedSeconds >= videoEndAt) {
      if (videoLoop) {
        videoPlayerRef.current?.seekTo(videoStartAt, "seconds");
      } else {
        setVideoPlaying(false);
      }
    }
  }, [videoEndAt, videoLoop, videoStartAt]);

  // Close video element
  const handleCloseVideo = useCallback((e) => {
    e?.stopPropagation();
    setActiveElement(null);
  }, [setActiveElement]);

  // Reset video state when element changes
  useEffect(() => {
    if (hasVideoElement) {
      setVideoReady(false);
      setVideoPlaying(false);
      setVideoMuted(videoAudioMode === "muted");
    }
  }, [hasVideoElement, videoAudioMode]);

  // Get branding settings for THIS specific participant.
  // For LOCAL: read from `branding` directly — it's the picker's authoritative
  // state, so toggling QR/logo off in BrandingPanel removes the overlay
  // immediately without waiting on the participantBranding map (which can
  // hold a stale entry after the toggle and re-show the overlay).
  // For REMOTE: use the synced participantBranding entry, trying numeric and
  // string key variants to tolerate sender-id format differences.
  const thisPtBranding = isLocal
    ? branding
    : (participantBranding?.[participantId]
        || participantBranding?.[String(participantId)]
        || participantBranding?.[stringToNumericUid(participantId)]);
  const micRef = useRef(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [safariSetSinkIdCalled, setSafariSetSinkIdCalled] = useState(false);

  // Check if this participant has raised their hand
  const hasRaisedHand = raisedHandsParticipants.some(
    (raisedHand) => raisedHand.participantId === participantId
  );

  // Check if this participant is BRB (Be Right Back)
  const isBRB = brbParticipants?.some(
    (brb) => brb.participantId === participantId
  );

  // Function to handle Safari setSinkId with user gesture
  const handleSafariSetSinkId = () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (
      isSafari &&
      micRef.current &&
      selectedSpeaker?.id &&
      !safariSetSinkIdCalled
    ) {
      try {
        micRef.current.setSinkId(selectedSpeaker.id);
        setSafariSetSinkIdCalled(true);
        console.log("Safari setSinkId called successfully");
      } catch (err) {
        console.log("Safari setSinkId failed:", err);
      }
    }
  };

  useEffect(() => {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (micRef.current && selectedSpeaker?.id) {
      try {
        if (!isFirefox && !isSafari) {
          // For non-Firefox and non-Safari browsers, setSinkId works normally
          micRef.current.setSinkId(selectedSpeaker.id);
        } else if (isSafari) {
          // For Safari, we need to wait for a user gesture
          // This will be handled when the user interacts with audio controls
          console.log(
            "Safari detected: setSinkId will be called on user interaction"
          );
        }
      } catch (err) {
        console.log("Setting speaker device failed", err);
      }
    }
  }, [selectedSpeaker]);

  useEffect(() => {
    if (micRef.current) {
      // Remote audio is played by Agora's audioTrack.play() in AgoraContext.js
      // Only set srcObject for local participant (muted, for level monitoring)
      if (isLocal && micOn && micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch((error) =>
            console.error("micRef.current.play() failed", error)
          );
      } else if (!isLocal) {
        micRef.current.srcObject = null;
      } else if (!micOn || !micStream) {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn, micRef, isLocal]);

  return mode === "SEND_AND_RECV" ? (
    <VideoFrameOverlay isActiveSpeaker={isActiveSpeaker} isSpotlighted={isSpotlighted}>
      <div
        onMouseEnter={() => {
          setMouseOver(true);
        }}
        onMouseLeave={() => {
          setMouseOver(false);
        }}
        className={`select-none w-full h-full bg-gray-900 relative overflow-hidden rounded-xl ${
          isSingleParticipant ? "video-cover-single" : "video-cover"
        }`}
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <audio
          ref={micRef}
          autoPlay
          muted={isLocal}
          onClick={handleSafariSetSinkId}
          style={{ cursor: "pointer" }}
        />
        {/* Pin/Unpin button — shown on hover, local only (doesn't affect other participants) */}
        {mouseOver && !isLocal && !isMobile && pinParticipant && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isPinned) {
                unpinParticipant(participantId);
              } else {
                unpinParticipant(null); // Clear any existing pin
                pinParticipant(participantId);
              }
            }}
            className={`absolute top-2 right-2 z-20 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
              isPinned
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-black/60 text-white/80 hover:bg-black/80 hover:text-white backdrop-blur-sm'
            }`}
            title={isPinned ? "Unpin from focus" : "Pin to focus"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill={isPinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 17v5"/><path d="M9 2h6l-1.5 5h-3L9 2z"/><path d="M7.5 7h9l-1.5 5H9L7.5 7z"/><path d="M9 12l-1 5h8l-1-5"/>
            </svg>
            {isPinned ? "Unpin" : "Pin"}
          </button>
        )}
        {/* Pinned indicator — always visible when pinned */}
        {isPinned && !mouseOver && (
          <div className="absolute top-2 right-2 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-600/80 text-white text-[10px] font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 17v5"/><path d="M9 2h6l-1.5 5h-3L9 2z"/><path d="M7.5 7h9l-1.5 5H9L7.5 7z"/><path d="M9 12l-1 5h8l-1-5"/>
            </svg>
            Pinned
          </div>
        )}
        {webcamOn ? (
          <VideoPlayer
            participantId={participantId} // Required
            type="video" // "video" or "share"
            containerStyle={{
              height: "100%",
              width: "100%",
            }}
            className="h-full"
            classNameVideo="h-full"
            videoStyle={{}}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <div
              className="flex items-center justify-center rounded-full 2xl:h-[92px] h-[52px] 2xl:w-[92px] w-[52px]"
              style={{ background: `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}, ${branding?.accentColor || '#00c853'})` }}
            >
              {participant?.metaData?.profileImage ? (
                <img
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                  src={participant?.metaData?.profileImage}
                />
              ) : (
                <span className="text-2xl font-bold text-white">
                  {String(displayName).charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
        )}

      {/* Status Badge Overlay - Raised Hand or BRB */}
      {hasRaisedHand && (
        <StatusBadge status="hand_raised" />
      )}
      {!hasRaisedHand && isBRB && (
        <StatusBadge status="brb" />
      )}

      {/* Tile Reaction Badge - shows emoji reactions on participant tile */}
      {participantReactions[participantId] && (
        <TileReaction
          reaction={participantReactions[participantId]}
        />
      )}

      {!hasRaisedHand && !isBRB &&
        localPinnedParticipant.includes(participantId) &&
        !pinState.cam && (
          <div className={`absolute left-2 top-2 z-20`}>
            <div className="rounded-full p-2 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                width="20"
                height="20"
                fill="none"
                stroke="#fff"
                strokeWidth="32"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M368 384c0-70.7-57.3-128-128-128V192h32c8.8 0 16-7.2 16-16V48c0-8.8-7.2-16-16-16H112c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16h32v64C73.3 256 16 313.3 16 384c0 8.8 7.2 16 16 16h144v96c0 8.8 7.2 16 16 16s16-7.2 16-16v-96h144c8.8 0 16-7.2 16-16z" />
              </svg>
            </div>
          </div>
        )}

      {!hasRaisedHand && !isBRB && pinState.cam && (
        <div className={`absolute left-2 top-2 z-20`}>
          <div className="rounded-full p-2 shadow-lg">
            <svg
              width="20"
              height="20"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="#fff"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.295 19.562L16 22m1-6l3.758 2.098M19 12.5l3.026-.598M7.61 6.3a3 3 0 0 0-3.92 1.3l-1.38 2.79a3 3 0 0 0 1.3 3.91l6.89 3.597a1 1 0 0 0 1.342-.447l3.106-6.211a1 1 0 0 0-.447-1.341zM8 9V2"
              />
            </svg>
          </div>
        </div>
      )}

      <CornerDisplayName
        {...{
          isLocal,
          displayName,
          micOn,
          webcamOn,
          isPresenting: false,
          participantId,
          mouseOver,
          isActiveSpeaker,
        }}
      />

      {/* Webinar role badge — only shown in webinar sessions */}
      {meetingSettings?.roomType === "Webinar" && (() => {
        const ROLE_BADGE = {
          moderator:  { label: 'Host',      color: 'bg-purple-600' },
          'co-host':  { label: 'Co-Host',   color: 'bg-blue-600' },
          presenter:  { label: 'Presenter', color: 'bg-green-600' },
          speaker:    { label: 'Speaker',   color: 'bg-indigo-600' },
        };
        const effectiveRole = isCoHost?.(participantId) ? 'co-host' : (participant?.metaData?.meetingRole || (isLocal ? meetingRole : null));
        const badge = ROLE_BADGE[effectiveRole];
        if (!badge) return null;
        return (
          <div className={`absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${badge.color} shadow-md`}>
            {badge.label}
          </div>
        );
      })()}

      {/* Logo Overlay - Shown on any participant's tile that has branding enabled.
          For local: thisPtBranding is the picker's `branding` (authoritative).
          For remote: thisPtBranding is participantBranding[participantId], synced
          via RTM PubSub so all viewers see it on the right tile. */}
      {thisPtBranding?.showLogo && thisPtBranding?.logoUrl && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            top: thisPtBranding.logoPosition?.includes('top') || !thisPtBranding.logoPosition || thisPtBranding.logoPosition === 'top-right' ? '8px' : undefined,
            bottom: thisPtBranding.logoPosition?.includes('bottom') ? '40px' : undefined,
            left: thisPtBranding.logoPosition?.includes('left') ? '8px' : undefined,
            right: thisPtBranding.logoPosition?.includes('right') || !thisPtBranding.logoPosition ? '8px' : undefined,
          }}
        >
          <div className="bg-black/50 backdrop-blur-sm rounded-lg p-1.5">
            <img
              src={thisPtBranding.logoUrl}
              alt="Brand Logo"
              className="max-h-8 md:max-h-12 object-contain"
              style={{ maxWidth: '100px' }}
            />
          </div>
        </div>
      )}

      {/* QR Code Overlay - Rendered as HTML overlay (not composited into video stream) */}
      {thisPtBranding?.showQrCode && thisPtBranding?.qrUrl && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            top: thisPtBranding.qrPosition?.includes('top') ? '8px' : undefined,
            bottom: thisPtBranding.qrPosition?.includes('bottom') || !thisPtBranding.qrPosition || thisPtBranding.qrPosition === 'bottom-right' ? '40px' : undefined,
            left: thisPtBranding.qrPosition?.includes('left') ? '8px' : undefined,
            right: thisPtBranding.qrPosition?.includes('right') || !thisPtBranding.qrPosition ? '8px' : undefined,
            ...(thisPtBranding.qrPosition?.includes('center') ? { left: '50%', transform: 'translateX(-50%)', right: 'auto' } : {}),
            ...(thisPtBranding.qrPosition === 'middle-left' || thisPtBranding.qrPosition === 'middle-right' ? { top: '50%', transform: 'translateY(-50%)', bottom: 'auto' } : {}),
          }}
        >
          <div
            className="rounded-lg shadow-lg"
            style={{
              backgroundColor: thisPtBranding.qrBgColor || '#ffffff',
              padding: '4px',
              width: thisPtBranding.qrSize === 'small' ? '60px' : thisPtBranding.qrSize === 'large' ? '100px' : '80px',
              height: thisPtBranding.qrSize === 'small' ? '60px' : thisPtBranding.qrSize === 'large' ? '100px' : '80px',
            }}
          >
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=${(thisPtBranding.qrColor || '#001233').replace('#', '')}&bgcolor=${(thisPtBranding.qrBgColor || '#ffffff').replace('#', '')}&data=${encodeURIComponent(thisPtBranding.qrUrl)}`}
              alt="QR Code"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Video Element PiP Overlay - Shows video as small overlay inside participant's tile */}
      {hasVideoElement && videoUrl && (() => {
        // Calculate size based on pip_settings.size (15-40% range from slider)
        // Map to reasonable pixel values: 15% -> 120px, 25% -> 180px, 40% -> 280px
        const sizePercent = videoPipSize || 25;
        const calculatedWidth = isMobile
          ? `${Math.min(sizePercent + 20, 70)}%`  // Mobile: add 20% and cap at 70%
          : `${sizePercent}%`;  // Desktop: use exact percentage
        const calculatedMaxWidth = sizePercent >= 35 ? '320px' : sizePercent >= 25 ? '240px' : '160px';
        const calculatedMinWidth = sizePercent >= 30 ? '120px' : '90px';

        // Audio: determine if video should be muted based on audio_mode setting
        const shouldMute = videoMuted || videoAudioMode === 'muted';
        const effectiveVolume = videoAudioMode === 'muted' ? 0 :
                               videoAudioMode === 'background' ? Math.min(videoVolume, 0.3) :
                               videoVolume;

        return (
        <div
          className="absolute z-30 rounded-lg overflow-hidden group"
          style={{
            // Position based on pip_settings from video element
            ...(videoPipPosition === 'top_left' || videoPipPosition === 'top-left' ? { top: '8px', left: '8px' } : {}),
            ...(videoPipPosition === 'top_right' || videoPipPosition === 'top-right' ? { top: '8px', right: '8px' } : {}),
            ...(videoPipPosition === 'bottom_left' || videoPipPosition === 'bottom-left' ? { bottom: '32px', left: '8px' } : {}),
            ...(videoPipPosition === 'bottom_right' || videoPipPosition === 'bottom-right' || !videoPipPosition ? { bottom: '32px', right: '8px' } : {}),
            // Size - based on pip_settings.size (percentage of tile width)
            width: calculatedWidth,
            maxWidth: calculatedMaxWidth,
            minWidth: calculatedMinWidth,
            aspectRatio: '16/9',
            border: '2px solid rgba(106, 31, 191, 0.8)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            backgroundColor: '#000',
          }}
        >
          <ReactPlayer
            ref={videoPlayerRef}
            url={videoUrl}
            width="100%"
            height="100%"
            playing={videoPlaying}
            muted={shouldMute}
            volume={effectiveVolume}
            loop={videoLoop}
            onReady={handleVideoReady}
            onEnded={handleVideoEnded}
            onProgress={handleVideoProgress}
            onError={(e) => console.error("Video element error:", e)}
            controls={false}
            playsinline
            progressInterval={500}
            config={{
              file: {
                attributes: {
                  preload: "auto",
                },
              },
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />

          {/* Video Controls Overlay - only shown to the person who enabled the video */}
          {isLocal && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-1">
            {/* Top: Close button */}
            <div className="flex justify-end">
              <button
                onClick={handleCloseVideo}
                className="w-5 h-5 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white transition-colors"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>

            {/* Bottom: Play controls */}
            <div className="flex items-center justify-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); setVideoPlaying(!videoPlaying); }}
                className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              >
                {videoPlaying ? (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setVideoMuted(!videoMuted); }}
                className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              >
                {videoMuted ? (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Loading Spinner */}
          {!videoReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        );
      })()}
      </div>
    </VideoFrameOverlay>
  ) : null;
}